import {
  Badge,
  Box,
  HStack,
  IconButton,
  Link,
  Text,
  Tooltip,
  useClipboard,
} from "@chakra-ui/react";
import { FaCheck, FaRegCopy } from "react-icons/fa";

interface DetailRowProps {
  label: string;
  value: string;
  valueColor?: string;
  valueHref?: string;
  copyable?: boolean;
  copyValue?: string;
  valueTooltip?: string;
  badge?: boolean;
  badgeColorScheme?: string;
  valueFontSize?: string;
}

export function DetailRow({
  label,
  value,
  valueColor = "#1E293B",
  valueHref,
  copyable = false,
  copyValue,
  valueTooltip,
  badge = false,
  badgeColorScheme = "green",
  valueFontSize = "sm",
}: DetailRowProps) {
  const { hasCopied, onCopy } = useClipboard(copyValue ?? valueHref ?? value);

  const valueProps = {
    textAlign: "right" as const,
    fontSize: valueFontSize,
    fontWeight: "normal" as const,
    color: valueColor,
    fontFamily: "Lexend Deca",
    wordBreak: "break-word" as const,
  };

  return (
    <HStack justify="space-between" align="center" spacing={6}>
      <Text
        fontSize="sm"
        fontWeight="500"
        color="#64748B"
        fontFamily="Lexend Deca"
        whiteSpace="nowrap"
        flexShrink={0}
      >
        {label}
      </Text>
      <HStack spacing={2} align="center" justify="flex-end">
        {(() => {
          const valueElement = badge ? (
            <Badge
              colorScheme={badgeColorScheme}
              px={2.5}
              py={1}
              borderRadius="md"
              fontSize="sm"
              fontWeight="500"
              fontFamily="Lexend Deca"
              textTransform="none"
            >
              {value}
            </Badge>
          ) : valueHref ? (
            <Link
              href={valueHref}
              isExternal
              {...valueProps}
              color="secondary"
              textDecoration="underline"
            >
              {value}
            </Link>
          ) : (
            <Text {...valueProps}>{value}</Text>
          );

          return valueTooltip ? (
            <Tooltip label={valueTooltip} placement="top" hasArrow>
              {valueElement}
            </Tooltip>
          ) : (
            valueElement
          );
        })()}
        <Box w="32px" flexShrink={0} display="flex" justifyContent="flex-end">
          {copyable ? (
            <Tooltip label={hasCopied ? "Copiado!" : "Copiar"} placement="top" hasArrow>
              <IconButton
                aria-label="Copiar"
                icon={hasCopied ? <FaCheck /> : <FaRegCopy />}
                size="sm"
                fontSize="md"
                variant="ghost"
                color={hasCopied ? "green.500" : "secondary"}
                onClick={onCopy}
              />
            </Tooltip>
          ) : null}
        </Box>
      </HStack>
    </HStack>
  );
}
